import React, { useState, useCallback } from 'react'
import { Box, TextField, IconButton, CircularProgress } from '@mui/material'
import SendIcon from '@mui/icons-material/Send'
import { useToast } from '@/shared/contexts/ToastContext'
import { useSendMessageMutation } from '@/features/internal/patient-messaging/hooks/mutations/useSendMessageMutation'

interface LineChatInputProps {
  threadId: string
}

const LineChatInput: React.FC<LineChatInputProps> = ({ threadId }) => {
  const [content, setContent] = useState('')
  const { showToast } = useToast()
  const sendMutation = useSendMessageMutation()

  const handleSend = useCallback(async () => {
    const trimmed = content.trim()
    if (!trimmed || sendMutation.isPending) return

    try {
      await sendMutation.mutateAsync({
        threadId,
        content: trimmed,
      })
      setContent('')
    } catch {
      showToast('メッセージの送信に失敗しました', 'error')
    }
  }, [content, threadId, sendMutation, showToast])

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault()
        handleSend()
      }
    },
    [handleSend]
  )

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-end',
        gap: 1,
        p: 1.5,
        borderTop: '1px solid',
        borderColor: 'divider',
      }}
    >
      <TextField
        fullWidth
        multiline
        maxRows={4}
        size="small"
        placeholder="メッセージを入力..."
        value={content}
        onChange={(e) => setContent(e.target.value)}
        onKeyDown={handleKeyDown}
        disabled={sendMutation.isPending}
      />
      <IconButton
        color="primary"
        onClick={handleSend}
        disabled={!content.trim() || sendMutation.isPending}
        size="small"
      >
        {sendMutation.isPending ? <CircularProgress size={20} /> : <SendIcon />}
      </IconButton>
    </Box>
  )
}

export default LineChatInput
